import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BillingUserContextService, DashboardProfileContextService } from '@rma/authentication/public/user';
import { CurrentSubscriptionService } from '@rma/billing/subscription-plans/public/api-subscription-features';
import { PaymentMethodAlertStatus, PaymentMethodClient } from '@rma/billing-api';
import { applyWithDialogDefaults } from '@rma/generic/util/material';
import { objectDistinctUntilChanged } from '@rma/generic/util/operators/object-distinct-until-changed';
import { rmaShareReplay } from '@rma/generic/util/operators/rma-share-replay';
import { addDays, isPast } from 'date-fns';
import { filter, map, merge, Observable, ReplaySubject, Subscription, switchMap, take } from 'rxjs';

import { PaymentMethodAlertDialogComponent } from './orc-payment-method-alert-dialog/payment-method-alert-dialog.component';

@Injectable()
export class PaymentMethodAlertService {
  public hasAlert$: Observable<boolean>;

  private subscription?: Subscription;
  private hasAlert = new ReplaySubject<boolean>();

  public constructor(
    private readonly currentSubscriptionService: CurrentSubscriptionService,
    private readonly billingUserContextService: BillingUserContextService,
    private readonly profileContextService: DashboardProfileContextService,
    private readonly paymentMethodClient: PaymentMethodClient,
    private readonly dialog: MatDialog,
  ) {
    this.hasAlert$ = this.hasAlert.asObservable();
  }

  public checkForAlert() {
    if (this.subscription) {
      return;
    }

    const billingContext$ = this.billingUserContextService.userContext$.pipe(rmaShareReplay());

    const isValidSub$ = billingContext$.pipe(
      switchMap(() =>
        this.currentSubscriptionService.currentSubscriptionSummary$.pipe(
          map((sub) => !!sub && !sub.isFreeSubscription && sub.isOwnSubscription),
          rmaShareReplay(),
        ),
      ),
    );

    const isNotDelayed$ = isValidSub$.pipe(
      filter(Boolean),
      switchMap((_) => this.profileContextService.profileUserTypeAndProfileCode$),
      map((context) => !this.isDelayed(context)),
    );

    const alertStatus$ = isNotDelayed$.pipe(
      filter(Boolean),
      switchMap((_) => billingContext$),
      objectDistinctUntilChanged(),
      switchMap((context) =>
        this.paymentMethodClient.requiresAttention(context.productUserType, context.productUserGroupingType, context.code),
      ),
      map((alertStatus) => ({ alertStatus, shouldAlert: alertStatus !== PaymentMethodAlertStatus.None })),
      rmaShareReplay(),
    );

    const shouldAlert$ = alertStatus$.pipe(map(({ shouldAlert }) => shouldAlert));

    merge(isValidSub$, isNotDelayed$, shouldAlert$).subscribe((potentialPaymentAlert) => {
      if (!potentialPaymentAlert) {
        this.hasAlert.next(false);
      }
    });

    this.subscription = alertStatus$.subscribe(({ alertStatus, shouldAlert }) => {
      if (shouldAlert) {
        this.dialog.open(PaymentMethodAlertDialogComponent, applyWithDialogDefaults({ data: alertStatus }));
        this.hasAlert.next(true);
      }
    });
  }

  public delayAlert(days: number) {
    this.profileContextService.profileUserTypeAndProfileCode$.pipe(take(1)).subscribe((x) => {
      localStorage?.setItem(`rmaPaymentAlert:${x.userType}:${x.profileCode}`, addDays(new Date(), days).toISOString());
    });
  }

  private isDelayed(context: { userType: 'Agent' | 'Agency'; profileCode: string }): boolean {
    const value = localStorage?.getItem(`rmaPaymentAlert:${context.userType}:${context.profileCode}`);

    if (value) {
      const date = new Date(value);

      return !isPast(date);
    }
    return false;
  }
}
